import { cacheKeys } from "@constants/cache-keys";
import { useCallback, useEffect, useState } from "react";

import Container from "@memorang/ui/components/Container";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { Redirect, router, useLocalSearchParams } from "expo-router";

import { events } from "@constants/tracking";
import { createRelativeURL } from "@helpers/url";
import { composeMessage } from "@memorang/helpers/email";
import { trackCustomEvent } from "analytics";

import { useRedeemCopilot } from "@features/redeem-copilot";
import { COPILOT_CHOICES } from "@features/redeem-copilot/instructions";

import { getSupportDetails, handleAccessCodeResult } from "../helpers";

import {
	useRcContactSupport,
	useRcGetPurchases,
	useRcMatchEmail,
	useRcRedeemCode,
	useRcResendCodes,
} from "@features/redeem-copilot/hooks";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import { validateCode } from "@helpers/string-utils";
import RedeemCodeView from "@memorang/ui/components/RedeemCodeView";
import { useQueryClient } from "@tanstack/react-query";
import { applyAccessCode } from "api/commerce/access-code-service";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
import { refreshExamsListCache } from "api/exams-query";
import { useTranslation } from "react-i18next";
import CollectUserDetailsDialogContainer from "./CollectUserDetailsDialogContainer";
import PostRedeemOlxDialog from "./PostRedeemOlxDialog";
import SessionHelpDialog from "./SessionHelpDialog";

type Props = {
	handleNext: (result: ApplyAccessCodeResponse) => void;
};

// Module scope: survives remounts of the whole redeem subtree (CopilotKitProvider
// remounts via key flip when auth headers resolve), which a ref cannot.
// TODO(redeem): refactor auto-submit so this guard becomes unnecessary — the effect
// should depend only on [pathCode, auth-ready] and submit pathCode via a stable
// callback, instead of depending on handleSubmit whose identity churns with `code`
// state and the fossil [email, appId] deps on handleApplyCode.
const autoSubmittedCodes = new Set<string>();

const RedeemCodeContainer = ({ handleNext }: Props) => {
	const supportEmail = useAppStore((store) => store.supportEmail);
	const company = useAppStore((store) => store.app.logo.company);
	const appName = useAppStore((store) => store.app.name);
	const appId = useAppStore((store) => store.app.id);
	const { t } = useTranslation();

	const [isCodeRedeemedBySameUser, setIsCodeRedeemedBySameUser] =
		useState(false);

	const [alreadyRedeemedProductType, setAlreadyRedeemedProductType] = useState<
		string | undefined
	>(undefined);

	const [helpDialogVisible, setHelpDialogVisible] = useState(false);

	const [showCollectUserDialog, setShowCollectUserDialog] = useState(false);

	const { setPartialChatState, pushContextMessage } = useRedeemCopilot();

	const toggleShowCollectUserDialog = () => {
		setShowCollectUserDialog((prev) => !prev);
	};

	const [olxDates, setOlxDates] = useState<{
		beginStartDate: string;
		endStartDate: string;
		tempAccessCodeResponse?: ApplyAccessCodeResponse;
	} | null>(null);

	const params = useLocalSearchParams<{
		redeemCode: string;
		showOnboarding: string;
	}>();

	const pathCode = params ? params.redeemCode : "";
	const showOnboarding = params ? params.showOnboarding : "";

	const theme = useAppTheme();

	const [code, setCode] = useState(pathCode);

	const {
		currentBlock: sessionItems,
		numSessionItems,
		numTotalItems,
	} = useCurrentSession();
	const examName = useSessionStore((state) => state.examName);
	const totalTime = useSessionStore((state) => state.totalTime);
	const numericId = useSessionStore((state) => state.numericId);

	useEffect(() => {
		setCode(pathCode);
	}, [pathCode]);

	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);

	const [loading, setLoading] = useState(false);

	const {
		isLoading: authLoading,
		userLogicalId,
		username,
		email,
		isSignedIn,
	} = useAuthContext();

	const { handleSwitchExamContext } = useExamContext();
	const queryClient = useQueryClient();

	const showError = errorMessage != null;

	// biome-ignore lint/correctness/useExhaustiveDependencies: Valid use case
	const handleApplyCode = useCallback(
		async ({ code }: { code: string }) => {
			const validationError = validateCode(code);

			if (validationError) {
				trackCustomEvent({
					eventName: events.promoCodeError,
					code,
					error: validationError,
				});

				return { success: false, error: validationError };
			}

			trackCustomEvent({
				eventName: events.promoCodeEntered,
				code,
			});

			try {
				const result = await applyAccessCode({ accessCode: code });
				if (result.success) {
					await refreshExamsListCache(queryClient);
					queryClient.invalidateQueries({
						queryKey: [cacheKeys.activeAccessCodes],
					});
				}
				const { shouldHandleNext } = await handleAccessCodeResult(
					result,
					handleSwitchExamContext,
					setOlxDates,
				);

				return {
					success: true,
					error: "",
					result,
					shouldHandleNext,
				};
			} catch (e) {
				let error = t("redeem.unexpectedError");

				if (e instanceof Error) {
					error = e.message;
				}

				if (
					e &&
					typeof e === "object" &&
					"response" in e &&
					e.response &&
					typeof e.response === "object" &&
					"data" in e.response &&
					e.response.data &&
					typeof e.response.data === "object" &&
					"message" in e.response.data &&
					typeof e.response.data.message === "string"
				) {
					error = e.response.data.message;
				}

				trackCustomEvent({
					eventName: events.promoCodeError,
					code,
					error,
				});

				return {
					success: false,
					error,
					shouldHandleNext: false,
				};
			}
		},
		[email, appId],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Valid use case
	const handleSubmit = useCallback(async () => {
		setLoading(true);
		const { success, error, result, shouldHandleNext } = await handleApplyCode({
			code,
		});

		setLoading(false);

		if (result?.isSameEmail) {
			setAlreadyRedeemedProductType(result.data.bundleType);
			setIsCodeRedeemedBySameUser(true);
			setErrorMessage(result.message);
			return;
		}
		if (success) {
			if (shouldHandleNext && result) {
				handleNext(result);
			}
			return;
		}

		setErrorMessage(error);
	}, [code, handleApplyCode]);

	const handleAskAssistant = useCallback(() => {
		if (isCodeRedeemedBySameUser) {
			pushContextMessage(
				JSON.stringify({
					type: "error",
					message: errorMessage,
					instructions: COPILOT_CHOICES.ACCESS_CODE_ALREADY_REDEEMED,
				}),
			);
			setPartialChatState({ chatOpen: true, isFirstTimeChatOpened: false });
			return;
		}

		if (errorMessage) {
			pushContextMessage(
				JSON.stringify({
					type: "error",
					message: errorMessage,
					instructions: COPILOT_CHOICES.REDEEM_ACCESS_CODE_ERROR,
				}),
			);
			setPartialChatState({ chatOpen: true, isFirstTimeChatOpened: false });
			return;
		}

		setPartialChatState({ chatOpen: true });
	}, [
		errorMessage,
		isCodeRedeemedBySameUser,
		pushContextMessage,
		setPartialChatState,
	]);

	const handlePressContactSupport = () => {
		const { subject, body } = getSupportDetails(
			appName,
			username ?? "",
			email || "",
		);
		composeMessage(subject, body, supportEmail);
	};

	useEffect(() => {
		if (
			pathCode &&
			isSignedIn &&
			!authLoading &&
			userLogicalId &&
			!autoSubmittedCodes.has(pathCode)
		) {
			autoSubmittedCodes.add(pathCode);
			handleSubmit();
		}
	}, [authLoading, handleSubmit, isSignedIn, pathCode, userLogicalId]);

	useEffect(() => {
		if (errorMessage && !isCodeRedeemedBySameUser) {
			setCode("");
		}
	}, [errorMessage, isCodeRedeemedBySameUser]);

	const handleChangeText = (text: string) => {
		if (isCodeRedeemedBySameUser) {
			setIsCodeRedeemedBySameUser(false);
		}

		if (showError) {
			setErrorMessage(undefined);
		}
		setCode(text);
	};

	const disableFab = !code || code.length < 6 || loading;

	const getCompanyLogo = () => {
		if (company) {
			const { light, dark } = company;
			return theme.dark ? dark : light;
		}
		return null;
	};

	const logo = getCompanyLogo();

	useRcRedeemCode({
		handler: handleApplyCode,
	});
	useRcResendCodes();
	useRcGetPurchases();
	useRcMatchEmail();
	useRcContactSupport({
		supportDetails: {
			supportEmail,
			appName,
			...getSupportDetails(appName, username ?? "", email || ""),
		},
		handler: handlePressContactSupport,
	});

	if (authLoading) {
		return null;
	}

	if (!isSignedIn) {
		const newUrl = createRelativeURL("/(auth)/login", params);

		return <Redirect href={newUrl} />;
	}

	return (
		// biome-ignore lint/a11y/useSemanticElements: <explanation>
		<Container maxWidth="xs" role="main">
			<RedeemCodeView
				title={t("redeemCode.formTitle")}
				logo={logo}
				defaultCode={pathCode}
				value={code}
				handleChangeText={handleChangeText}
				handleSubmit={handleSubmit}
				disableFab={disableFab}
				isCodeRedeemedBySameUser={isCodeRedeemedBySameUser}
				loading={loading}
				handleAskAssistant={handleAskAssistant}
				email={email || ""}
				errorMessage={errorMessage}
				showError={showError}
				showOnboarding={showOnboarding}
				handlePressContactSupport={handlePressContactSupport}
				contactSupportTemplate={t("redeem.contactSupportHint")}
				onNavigateToAlreadyRedeemedProduct={() => {
					const path =
						alreadyRedeemedProductType === "PRACTICE_TEST"
							? "/practice-tests"
							: "/study-pack";
					router.push(path);
				}}
				onSkipOnboarding={() => router.replace("/onboarding")}
			/>
			<SessionHelpDialog
				helpDialogVisible={helpDialogVisible}
				sessionItems={sessionItems}
				examName={examName}
				totalTime={totalTime}
				numericId={numericId}
				setHelpDialogVisible={setHelpDialogVisible}
				numSections={numSessionItems}
				numTotalQuestions={numTotalItems}
			/>
			<PostRedeemOlxDialog
				olxDates={olxDates}
				setOlxDates={setOlxDates}
				handleNext={handleNext}
				toggleShowCollectUserDialog={toggleShowCollectUserDialog}
			/>
			<CollectUserDetailsDialogContainer
				showCollectUserDialog={showCollectUserDialog}
				toggleShowCollectUserDialog={toggleShowCollectUserDialog}
				setHelpDialogVisible={setHelpDialogVisible}
			/>
		</Container>
	);
};

export default RedeemCodeContainer;
